import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Drawer } from 'antd'
import { Alert, Button, message } from '@surecompanies/core_components'
import dayjs from 'dayjs'

import {
  useBenefitTickets,
  useLinkZendeskTicket,
  useUnlinkZendeskTicket,
} from '../../api/reconciliation-v2-api-client'
import { colors, font, radius } from './tokens'
import styles from './ZendeskTicketModal.module.scss'
import { useCanWriteReconciliation } from './useCanWriteReconciliation'
import {
  buildPeriodMonthOptions,
  isValidUrl,
} from './ZendeskTicketModal.helpers'
import {
  CloseIcon,
  ExternalLinkIcon,
  PlusIcon,
  TicketIcon,
} from './ZendeskTicketModal.icons'

// Design hex with no token equivalent (Table-Option1.dc.html, support tickets
// side panel) — drawer border-left.
const DRAWER_EDGE = '#E1E5E9'

/**
 * One editable ticket row. `id` is the existing NOTE_ADDED event id (null for a
 * card the operator just added). orig* snapshots the persisted values so Done
 * can tell an edit (unlink old + link new) from an untouched row.
 */
interface TicketRecord {
  key: string
  id: string | null
  url: string
  month: string
  note: string
  origUrl: string
  origMonth: string
  origNote: string
}

interface Props {
  open: boolean
  onClose: () => void
  benefitId: string | null
  /** When set, seeds the month for newly-added cards. */
  periodStartDate: string | null
  employeeName?: string | null
}

const ZendeskTicketModal: React.FC<Props> = ({
  open,
  onClose,
  benefitId,
  periodStartDate,
  employeeName,
}) => {
  const defaultMonth =
    periodStartDate ?? dayjs().startOf('month').format('YYYY-MM-DD')

  // Write gate — mirrors the EPS AbilityGuard(Write, PaymentReconciliation) on
  // link/unlink-zendesk-ticket. Non-writers see the linked tickets read-only.
  const { allowed: canWrite } = useCanWriteReconciliation()

  // Built inside the component (memoized on first render) so the list reflects
  // the current date at render time, not module-import time.
  const monthOptions = useMemo(
    () =>
      buildPeriodMonthOptions().map((m) => ({
        label: dayjs(m).format('MMMM YYYY'),
        value: m,
      })),
    []
  )

  const { data: existing, isError: isTicketsError } = useBenefitTickets(
    benefitId,
    {
      enabled: open && !!benefitId,
    }
  )

  const [records, setRecords] = useState<TicketRecord[]>([])
  const [saving, setSaving] = useState(false)

  const link = useLinkZendeskTicket({
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message
      message.error(`Failed to link ticket: ${msg ?? 'unknown error'}`)
    },
  })

  const unlink = useUnlinkZendeskTicket({
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message
      message.error(`Failed to remove ticket: ${msg ?? 'unknown error'}`)
    },
  })

  // Reset when the drawer closes OR the benefit changes, so switching benefits
  // while the drawer is open re-hydrates from scratch instead of leaving the
  // previous benefit's tickets on screen (which saves would write against the
  // new benefitId). Kept in its own effect keyed on [open, benefitId] so it
  // does NOT fire on refetch/data-arrival — only the hydrate effect below does.
  const hydrated = useRef(false)
  useEffect(() => {
    hydrated.current = false
    setRecords([])
  }, [open, benefitId])

  // Hydrate the editable list once per open, when the existing tickets first
  // load. The `hydrated` ref guards against re-seeding on every refetch
  // (Remove/Done invalidate the query), which would clobber in-progress edits.
  // Unsaved rows the operator added while the fetch was still in flight are
  // preserved (merged after the fetched rows) instead of being wiped.
  useEffect(() => {
    if (!open || hydrated.current || existing === undefined) return
    hydrated.current = true
    setRecords((prev) => {
      const unsaved = prev.filter((r) => r.id === null)
      const fetched = existing.map((t, i) => ({
        key: t.id ?? `ex-${i}`,
        id: t.id ?? null,
        url: t.ticketUrl,
        month: t.periodStartDate,
        note: t.note ?? '',
        origUrl: t.ticketUrl,
        origMonth: t.periodStartDate,
        origNote: t.note ?? '',
      }))
      return [...fetched, ...unsaved]
    })
  }, [open, existing])

  const addRecord = () => {
    setRecords((prev) => [
      ...prev,
      {
        key: `new-${Date.now()}-${prev.length}`,
        id: null,
        url: '',
        month: defaultMonth,
        note: '',
        origUrl: '',
        origMonth: '',
        origNote: '',
      },
    ])
  }

  const updateRecord = (key: string, patch: Partial<TicketRecord>) => {
    setRecords((prev) =>
      prev.map((r) => (r.key === key ? { ...r, ...patch } : r))
    )
  }

  const removeRecord = async (key: string) => {
    const rec = records.find((r) => r.key === key)
    if (!rec) return
    // Persisted rows unlink immediately; unsaved cards just drop.
    if (rec.id && benefitId) {
      try {
        await unlink.mutateAsync({
          benefitId,
          periodStartDate: rec.origMonth,
          ticketUrl: rec.origUrl,
        })
        message.success('Removed support ticket.')
      } catch {
        return
      }
    }
    setRecords((prev) => prev.filter((r) => r.key !== key))
  }

  const isDirty = (r: TicketRecord): boolean =>
    r.url.trim() !== r.origUrl ||
    r.month !== r.origMonth ||
    r.note.trim() !== r.origNote.trim()

  const handleDone = async () => {
    if (!benefitId) {
      onClose()
      return
    }
    // Validate every row that still carries a URL before persisting anything.
    const withUrl = records.filter((r) => r.url.trim().length > 0)
    for (const r of withUrl) {
      if (!isValidUrl(r.url)) {
        message.warning('Ticket URL must start with http(s)://')
        return
      }
      if (!r.month) {
        message.warning('Pick the month each ticket is about')
        return
      }
    }
    const ops: Array<Promise<unknown>> = []
    for (const r of records) {
      if (!isDirty(r)) continue
      const url = r.url.trim()
      const note = r.note.trim() || undefined
      const hadPersistedUrl = Boolean(r.id) && r.origUrl.length > 0

      if (url.length === 0) {
        // URL cleared. For a persisted row this must unlink the ORIGINAL
        // (period,url) — otherwise Done closes while the server keeps the link
        // even though the UI looked cleared. An empty brand-new row is a no-op.
        if (hadPersistedUrl) {
          ops.push(
            unlink.mutateAsync({
              benefitId,
              periodStartDate: r.origMonth,
              ticketUrl: r.origUrl,
            })
          )
        }
        continue
      }

      if (!r.id) {
        // Brand-new link.
        ops.push(
          link.mutateAsync({
            benefitId,
            periodStartDate: r.month,
            ticketUrl: url,
            note,
          })
        )
        continue
      }

      // Persisted row still carrying a URL. unlink is keyed by (period,url), so
      // whether we may unlink the original depends on whether that key changed.
      const sameLinkKey = r.month === r.origMonth && url === r.origUrl
      if (sameLinkKey) {
        // Only the note changed (period+url identical). Re-link with the same
        // key to update the note, and DO NOT emit an unlink — an unlink of the
        // same (period,url) would remove the link we just saved (the HIGH bug).
        ops.push(
          link.mutateAsync({
            benefitId,
            periodStartDate: r.month,
            ticketUrl: url,
            note,
          })
        )
      } else {
        // The URL and/or month actually changed. Link the new value first, then
        // unlink the original. This ordering ensures the period always has at
        // least one ticket link: if link succeeds but unlink fails the ticket is
        // duplicated (harmless), whereas if unlink ran first a failure would
        // leave the period with no ticket (data-loss). Because the new
        // (period,url) differs from the original, this unlink cannot cancel the
        // link we just saved. The backend exposes link/unlink only — no in-place update.
        ops.push(
          link
            .mutateAsync({
              benefitId,
              periodStartDate: r.month,
              ticketUrl: url,
              note,
            })
            .then(() =>
              unlink.mutateAsync({
                benefitId,
                periodStartDate: r.origMonth,
                ticketUrl: r.origUrl,
              })
            )
        )
      }
    }
    if (ops.length === 0) {
      onClose()
      return
    }
    setSaving(true)
    try {
      await Promise.all(ops)
      message.success(
        `Saved ${ops.length} support ticket${ops.length > 1 ? 's' : ''}.`
      )
      onClose()
    } catch {
      /* per-op error toasts already fired */
    } finally {
      setSaving(false)
    }
  }

  const hasAny = records.length > 0

  const title = (
    <div className={styles.headerRow}>
      <div>
        <div className={styles.headerTitle}>Support tickets</div>
        {employeeName && (
          <div className={styles.headerSubtitle}>{employeeName}</div>
        )}
      </div>
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className={styles.closeBtn}
      >
        <CloseIcon size={18} />
      </button>
    </div>
  )

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width={440}
      title={title}
      closable={false}
      footer={
        <Button
          type="primary"
          onClick={handleDone}
          disabled={!canWrite}
          loading={saving}
          style={{
            width: '100%',
            height: 42,
            background: colors.brand.primary,
            borderColor: colors.brand.primary,
            borderRadius: radius.md,
            fontFamily: font.sans,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          Done
        </Button>
      }
      styles={{
        content: { borderLeft: `1px solid ${DRAWER_EDGE}` },
        header: {
          padding: '22px 24px 18px',
          borderBottom: `1px solid ${colors.border.subtle}`,
        },
        body: { padding: '18px 24px 8px' },
        footer: {
          borderTop: `1px solid ${colors.border.subtle}`,
          padding: '16px 24px',
        },
      }}
    >
      {isTicketsError && (
        <Alert
          type="error"
          message="Failed to load tickets"
          description="Could not fetch support tickets for this benefit. Please close and try again."
          style={{ marginBottom: 16 }}
        />
      )}

      {!isTicketsError && !hasAny && (
        <div className={styles.emptyState}>
          <TicketIcon />
          <span className={styles.emptyText}>
            No support tickets linked yet.
          </span>
        </div>
      )}

      <div className={styles.cards}>
        {records.map((r, i) => {
          const canOpen = isValidUrl(r.url)
          return (
            <div className={styles.card} key={r.key}>
              <div className={styles.cardHead}>
                <span className={styles.ticketLabel}>{`TICKET ${i + 1}`}</span>
                <button
                  type="button"
                  onClick={() => void removeRecord(r.key)}
                  disabled={unlink.isPending || !canWrite}
                  className={styles.removeBtn}
                >
                  <CloseIcon />
                  Remove
                </button>
              </div>
              <div className={styles.linkRow}>
                <input
                  value={r.url}
                  onChange={(e) => updateRecord(r.key, { url: e.target.value })}
                  placeholder="https://… ticket URL"
                  disabled={!canWrite}
                  className={styles.linkInput}
                />
                <a
                  href={canOpen ? r.url.trim() : undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open ticket"
                  className={`${styles.openLink}${
                    canOpen ? ` ${styles.enabled}` : ''
                  }`}
                >
                  <ExternalLinkIcon />
                </a>
              </div>
              <select
                value={r.month}
                onChange={(e) => updateRecord(r.key, { month: e.target.value })}
                disabled={!canWrite}
                className={styles.monthSelect}
              >
                {monthOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <textarea
                value={r.note}
                onChange={(e) => updateRecord(r.key, { note: e.target.value })}
                rows={2}
                placeholder="Add a note…"
                disabled={!canWrite}
                className={styles.noteArea}
              />
            </div>
          )
        })}
      </div>

      <button
        type="button"
        onClick={addRecord}
        disabled={!canWrite}
        className={styles.addTicketBtn}
      >
        <PlusIcon />
        Add ticket link
      </button>
    </Drawer>
  )
}

export default ZendeskTicketModal
